import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Dumbbell, Sparkles, Truck, UtensilsCrossed, Megaphone, Calculator, Layers } from "lucide-react";
import { INVICTAX_URL } from "@/components/PoweredByInvictaX";

const PLATFORMS = [
  {
    icon: Dumbbell,
    name: "StrongFit Studio",
    purpose: "Komplettlösung für den Betrieb von Fitnessstudios.",
    audience: "Fitnessstudios, Personal Trainer und Studio-Ketten",
    features: ["Mitgliedschaften & Abo-Verwaltung", "Kursplanung & Buchungen", "Zutritt & Check-in", "Abrechnung & Mahnwesen"],
  },
  {
    icon: Sparkles,
    name: "ReinMove",
    purpose: "Digitale Auftrags- und Einsatzplanung für Reinigungs- und Umzugsdienstleistungen.",
    audience: "Reinigungsfirmen, Umzugsunternehmen und Facility-Services",
    features: ["Offerten & Aufträge", "Einsatz- & Teamplanung", "Rapportierung vor Ort", "Rechnungsstellung"],
  },
  {
    icon: Truck,
    name: "TrucksOnRoad",
    purpose: "Disposition und Flottenmanagement für Transportunternehmen.",
    audience: "Transport-, Logistik- und Kurierunternehmen",
    features: ["Tourenplanung & Disposition", "Fahrzeug- & Fahrerverwaltung", "Auftragsverfolgung", "Abrechnung"],
  },
  {
    icon: UtensilsCrossed,
    name: "Bella Strada",
    purpose: "Digitale Lösung für Gastronomie- und Streetfood-Betriebe.",
    audience: "Restaurants, Foodtrucks und Catering-Anbieter",
    features: ["Bestellungen & Kasse", "Menü- & Standortverwaltung", "Events & Catering", "Auswertungen"],
  },
  {
    icon: Megaphone,
    name: "ZO-Werbeflächen",
    purpose: "Vermarktung und Verwaltung von Werbeflächen im Zürcher Oberland.",
    audience: "Werbetreibende, Flächenbesitzer und Agenturen",
    features: ["Flächen-Katalog & Buchung", "Belegungsplanung", "Verträge & Laufzeiten", "Abrechnung"],
  },
  {
    icon: Calculator,
    name: "Buchhaltung CH",
    purpose: "KI-gestützte Buchhaltungssoftware für Schweizer KMU — diese Plattform.",
    audience: "GmbHs, Einzelfirmen, Holdings und Treuhänder",
    features: ["KI-Belegerfassung (OCR, WhatsApp, E-Mail)", "Bankabgleich camt.053 & QR-Rechnung", "Lohnbuchhaltung & Jahresabschluss", "Multi-Company & Konsolidierung"],
  },
];

export default function Plattformen() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-slate-900">
            Buchhaltung CH
          </Link>
          <nav className="text-sm space-x-4 text-slate-600">
            <Link to="/impressum" className="hover:text-slate-900">Impressum</Link>
            <Link to="/privacy" className="hover:text-slate-900">Datenschutz</Link>
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700">Anmelden</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12" data-testid="plattformen-page">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1 mb-4">
            <Layers className="h-3.5 w-3.5" />
            InvictaX Group GmbH
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Unsere Softwarelösungen
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Die InvictaX Group GmbH entwickelt, betreibt und lizenziert digitale Plattformen für
            verschiedene Branchen — von Fitness über Logistik bis zur Buchhaltung. Alle Lösungen
            teilen dieselbe sichere, in der Schweiz betreute Infrastruktur.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PLATFORMS.map((p) => (
            <Card
              key={p.name}
              className="p-6 border border-slate-200 bg-white flex flex-col"
              data-testid={`platform-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <p.icon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">{p.name}</h2>
              </div>
              <p className="text-sm text-slate-700 mb-2">{p.purpose}</p>
              <p className="text-xs text-slate-500 mb-3">
                <strong className="text-slate-600">Zielgruppe:</strong> {p.audience}
              </p>
              <ul className="text-sm text-slate-600 space-y-1 mb-4">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-auto pt-3 border-t border-slate-100 text-[11px] text-slate-400">
                Entwickelt, betrieben und lizenziert durch die InvictaX Group GmbH.
              </p>
            </Card>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm text-slate-600">
            Weitere Plattformen sind in Entwicklung.{" "}
            <a
              href={INVICTAX_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Mehr über die InvictaX Group GmbH →
            </a>
          </p>
        </div>
      </main>

      <footer className="max-w-5xl mx-auto px-6 pb-10 text-xs text-slate-400">
        © {new Date().getFullYear()} InvictaX Group GmbH — Softwareplattform und digitale
        Infrastruktur entwickelt, betrieben und lizenziert durch InvictaX Group GmbH. ·{" "}
        <Link to="/impressum" className="hover:text-slate-600">Impressum</Link>
      </footer>
    </div>
  );
}
